import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daf9ekbep053ijub1ls0";

export default function Subscribe() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const hp = (fd.get("website_alt") as string) || "";
    if (hp.trim() !== "") {
      // silently behave like a normal success to hide honeypot signal from bots
      setStatus("success");
      return;
    }
    fd.delete("website_alt");
    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = (parsed as { code?: string })?.code;
      const serverMsg =
        (parsed as { meta?: { message?: string; detail?: string } })?.meta?.message ||
        (parsed as { message?: string })?.message ||
        (parsed as { meta?: { detail?: string } })?.meta?.detail ||
        "";
      const looksSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg);

      if (!res.ok || code !== "OK" || looksSpam) {
        setStatus("error");
        setFormError(serverMsg || "订阅未成功，请稍后再试。");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络开小差了，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-950 scroll-mt-24">
      <div className="container-x grid grid-cols-1 items-center gap-10 py-12 md:py-16 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.2em] text-accent-400">
            <i className="ri-mail-open-fill" aria-hidden="true" />
            订阅吃瓜资讯
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
            不错过每一次<br className="hidden md:block" />出圈的重要节点
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-7 text-background-300">
            每周精选走红事件、热门主角档案与热点复盘，直送到你的邮箱。内容原创、克制、只讲真实故事。
          </p>
          <ul className="mt-6 space-y-3 text-sm text-background-200">
            <li className="flex items-center gap-2.5">
              <i className="ri-checkbox-circle-line text-accent-400" aria-hidden="true" />
              每周一期精选走红事件盘点
            </li>
            <li className="flex items-center gap-2.5">
              <i className="ri-checkbox-circle-line text-accent-400" aria-hidden="true" />
              新晋主角与爆红人物档案首发
            </li>
            <li className="flex items-center gap-2.5">
              <i className="ri-checkbox-circle-line text-accent-400" aria-hidden="true" />
              随时可退订，绝不打扰
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-background-800 bg-background-900/40 p-6 md:p-8">
          {status === "success" ? (
            <div className="flex flex-col items-center py-6 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-background-950">
                <i className="ri-check-line text-xl" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold text-background-50">订阅成功！</h3>
              <p className="mt-2 text-sm text-background-300">欢迎加入吃瓜爆网，第一期精彩内容正在路上。</p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-5 font-label text-sm text-accent-400 hover:text-accent-300 cursor-pointer"
              >
                再订阅一个邮箱
              </button>
            </div>
          ) : (
            <>
              <h3 className="font-label text-base font-medium text-background-50">填写邮箱，开启追更</h3>
              <form id="newsletter-form" data-readdy-form="newsletter" onSubmit={onSubmit} className="mt-5">
                <div className="honeypot-zone" aria-hidden="true">
                  <label htmlFor="website_alt">Website</label>
                  <input
                    id="website_alt"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>
                <label htmlFor="nl-email" className="font-label text-xs text-background-300">
                  邮箱地址
                </label>
                <input
                  id="nl-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="mt-2 w-full rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm text-background-50 placeholder:text-background-500 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/30"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-accent mt-4 w-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? "正在提交…" : "立即订阅"}
                  {status !== "loading" && <i className="ri-send-plane-line" aria-hidden="true" />}
                </button>
                {status === "error" && (
                  <p className="mt-3 rounded-md bg-primary-500/15 px-3 py-2 text-sm text-primary-300">
                    {formError || "订阅失败，请稍后再试。"}
                  </p>
                )}
                <p className="mt-4 text-xs leading-5 text-background-400">
                  提交即表示你同意接收我们的资讯邮件，我们承诺不会向任何第三方分享你的信息。
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}